import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Paper,
  LoadingOverlay,
  Stack,
  Center,
  ScrollArea,
  Button,
  Group,
  Tabs,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { SurveyStep } from '@/components/survey/survey-step';
import { LoansProvider } from '@/context/loans-context';
import { processUploadedFile } from '@/utils/parser';
import { UserSurveyData } from '@/components/survey/user-survey';
import { ResultsStep } from '@/components/results/plan-summary/results-step';
import { FileErrorDisplay } from '@/components/data-input/file-error-display';
import { IconArrowRight, IconCalendarStats } from '@tabler/icons-react';

import { ParsedData, Loan } from './types/nsldsTypes';

import './App.css';

// Add type for iframe resizer
declare global {
  interface Window {
    parentIFrame?: {
      size: () => void;
    };
  }
}

const initialSurveyValues: UserSurveyData = {
  annualIncome: 140000,
  includePartner: false,
  partnerAnnualIncome: 140000,
  familySize: 1,
  state: 'CA',
  pslfEligible: false,
  pslfEligiblePartner: false,
  appointmentMade: false,
  filingStatus: 'MFJ',
};

// Simple debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function App() {
  const [userFile, setUserFile] = useState<File | null>(null);
  const [partnerFile, setPartnerFile] = useState<File | null>(null);
  const isMobile = useMediaQuery('(max-width: 520px)');

  const [userManualLoan, setUserManualLoan] = useState<Loan | null>({
    'Loan Award ID': 'USER_MANUAL_LOAN',
    Borrower: 'user',
    'Total Outstanding': 400000,
    'Outstanding Principal': 400000,
    'Outstanding Interest': 0,
    'Loan Interest Rate': 0.07,
    'Loan Type Group': 'Direct',
    'Current Loan Status Description': 'In Repayment',
    'Loan PSLF Cumulative Matched Months': 0,
    qualifiedPaymentMonths: 0,
    isInRepayment: true,
    originalPrincipal: 400000,
    degreeType: 'graduate',
  });
  const [partnerManualLoan, setPartnerManualLoan] = useState<Loan | null>({
    'Loan Award ID': 'PARTNER_MANUAL_LOAN',
    Borrower: 'partner',
    'Total Outstanding': 400000,
    'Outstanding Principal': 400000,
    'Outstanding Interest': 0,
    'Loan Interest Rate': 0.07,
    'Loan Type Group': 'Direct',
    'Current Loan Status Description': 'In Repayment',
    'Loan PSLF Cumulative Matched Months': 0,
    qualifiedPaymentMonths: 0,
    isInRepayment: true,
    originalPrincipal: 400000,
    degreeType: 'graduate',
  });

  const [isLoadingUserFile, setIsLoadingUserFile] = useState(false);
  const [isLoadingPartnerFile, setIsLoadingPartnerFile] = useState(false);
  const [userParsedData, setUserParsedData] = useState<ParsedData | null>(null);
  const [partnerParsedData, setPartnerParsedData] = useState<ParsedData | null>(null);
  const [userFileError, setUserFileError] = useState<string | null>(null);
  const [partnerFileError, setPartnerFileError] = useState<string | null>(null);

  const [surveyDataForContext, setSurveyDataForContext] =
    useState<UserSurveyData>(initialSurveyValues);
  const [activeMobileTab, setActiveMobileTab] = useState<string>('survey');

  const isLoading = isLoadingUserFile || isLoadingPartnerFile;

  const surveyForm = useForm<UserSurveyData>({
    initialValues: initialSurveyValues,
    validateInputOnChange: true,
    validate: {
      annualIncome: (value) => (!value && value !== 0 ? 'Annual income is required' : null),
      partnerAnnualIncome: (value, values) =>
        values.includePartner && (value == null || value < 0)
          ? 'Partner annual income is required when including partner'
          : null,
      familySize: (value, values) => {
        if (value == null || value < 1) {
          return 'Family size is required and must be at least 1';
        }
        if (values.includePartner && value < 2) {
          return 'Family size must be at least 2 when married';
        }
        return null;
      },
      state: (value) => (!value || value.trim() === '' ? 'State is required' : null),
      filingStatus: (value, values) =>
        values.includePartner && (!value || value.trim() === '')
          ? 'Filing status is required when including partner'
          : null,
    },
  });

  const debouncedSurveyFormValues = useDebounce(surveyForm.values, 0);

  const containerRef = useRef<HTMLDivElement>(null);

  // Function to trigger iframe resize
  const triggerIframeResize = useCallback(() => {
    if (window.parentIFrame?.size) {
      setTimeout(() => {
        window.parentIFrame?.size();
      }, 100);
    }
  }, []);

  useEffect(() => {
    // Dynamically load the iframeResizer.contentWindow.min.js script
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.9/iframeResizer.contentWindow.min.js'; // Using v4.3.9 for broad compatibility
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Clean up the script when the component unmounts
      document.head.removeChild(script);
    };
  }, []);

  const stableProcessItemFile = useCallback(
    async (
      file: File | null,
      setParsedData: (data: ParsedData | null) => void,
      setError: (error: string | null) => void,
      setIsLoadingFile: (loading: boolean) => void,
      borrower: 'user' | 'partner',
      otherBorrowerLoans: Loan[]
    ): Promise<boolean> => {
      if (!file) {
        setParsedData(null);
        setError(null);
        setIsLoadingFile(false);
        return false;
      }

      setIsLoadingFile(true);

      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
          const text = e.target?.result as string;
          if (!text) {
            const errorMsg = 'File content could not be read. Please try again.';
            console.error('FileReader onload: File content is empty or unreadable.');
            setError(errorMsg);
            setIsLoadingFile(false);
            resolve(false);
            return;
          }

          try {
            const data = await processUploadedFile(text, otherBorrowerLoans, borrower);
            setParsedData(data);
            setError(null); // Clear error on successful parse
            resolve(true);
          } catch (error) {
            console.error('Error parsing file:', error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'An unknown error occurred during processing.';
            const finalErrorMsg = `File processing error: ${errorMessage}`;
            setError(finalErrorMsg);
            resolve(false);
          } finally {
            setIsLoadingFile(false);
          }
        };

        reader.onerror = (error) => {
          const errorMsg = 'Error reading the uploaded file. Please try again.';
          console.error('Error reading file:', error);
          setError(errorMsg);
          setIsLoadingFile(false);
          resolve(false);
        };

        reader.readAsText(file);
      });
    },
    []
  );

  // Parse user file - only depends on userFile (no cross-dependency on partner data)
  useEffect(() => {
    void stableProcessItemFile(
      userFile,
      setUserParsedData,
      setUserFileError,
      setIsLoadingUserFile,
      'user',
      [] // Duplicate detection moved to loans-context.tsx
    );
  }, [userFile, stableProcessItemFile]);

  // Parse partner file - only depends on partnerFile (no cross-dependency on user data)
  useEffect(() => {
    if (surveyForm.values.includePartner) {
      void stableProcessItemFile(
        partnerFile,
        setPartnerParsedData,
        setPartnerFileError,
        setIsLoadingPartnerFile,
        'partner',
        [] // Duplicate detection moved to loans-context.tsx
      );
    } else {
      // If partner is no longer included, clear related partner data
      setPartnerFile(null);
      setPartnerParsedData(null);
      setPartnerFileError(null);
      setIsLoadingPartnerFile(false);
    }
  }, [partnerFile, surveyForm.values.includePartner, stableProcessItemFile]);

  // Update surveyDataForContext with debounced values.
  useEffect(() => {
    setSurveyDataForContext(debouncedSurveyFormValues);
  }, [debouncedSurveyFormValues]);

  function handleScheduleAppointment() {
    // TODO: Update bottom toolbar after clicking link. Should keep the Schedule button but also link to the upload modal
    // Or just update the bottom toolbar to just say "Schedul an appointment" or "already booked?"
    window.open(
      'https://calendly.com/grad-loan-advice/consult?utm_source=nslds-calculator',
      '_blank'
    );
    console.log('Appointment scheduled');
  }

  const canShowResults = useMemo(() => {
    const isFormValid = !surveyForm.validate().hasErrors;
    const hasPartner = surveyForm.values.includePartner;

    const hasUserLoanData = userParsedData || userManualLoan;
    const hasPartnerLoanData = partnerParsedData || partnerManualLoan;

    return (
      isFormValid &&
      hasUserLoanData &&
      (hasPartner ? hasPartnerLoanData : true) &&
      !isLoadingUserFile &&
      !(hasPartner && isLoadingPartnerFile) &&
      !userFileError &&
      !(hasPartner && partnerFile && partnerFileError)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surveyForm.values,
    userParsedData,
    partnerParsedData,
    userManualLoan,
    partnerManualLoan,
    userFileError,
    partnerFileError,
    isLoadingUserFile,
    isLoadingPartnerFile,
    partnerFile,
  ]);

  // Trigger iframe resize when results become available
  useEffect(() => {
    triggerIframeResize();
  }, [activeMobileTab, triggerIframeResize]);

  let surveyAndResultsContent;

  const surveyStepProps = {
    form: surveyForm,
    isMobile: isMobile,
    userFile,
    setUserFile,
    partnerFile,
    setPartnerFile,
    userManualLoan,
    setUserManualLoan,
    partnerManualLoan,
    setPartnerManualLoan,
    isLoadingUserFile,
    isLoadingPartnerFile,
    userFileError,
    setUserFileError,
    partnerFileError,
    setPartnerFileError,
    stableProcessItemFile,
    setUserParsedData,
    setPartnerParsedData,
    setIsLoadingUserFile,
    setIsLoadingPartnerFile,
    userParsedData,
    partnerParsedData,
  };

  if (isMobile) {
    surveyAndResultsContent = (
      <Stack gap="xl">
        <Button
          variant="gradient"
          gradient={{ from: 'blue', to: 'cyan' }}
          onClick={handleScheduleAppointment}
          leftSection={<IconCalendarStats size={20} />}
          rightSection={<IconArrowRight size={16} />}
          size="lg"
          disabled={isLoading}
          fullWidth
        >
          Book a consultation
        </Button>
        <Tabs
          variant="outline"
          value={activeMobileTab}
          onChange={(value) => {
            console.log('Tab changed to:', value);
            if (value) setActiveMobileTab(value);
            triggerIframeResize(); // Resize on manual tab change
          }}
        >
          <Tabs.List grow>
            <Tabs.Tab value="survey">Your information</Tabs.Tab>
            <Tabs.Tab value="results" disabled={!canShowResults}>
              Results
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="survey" pt="sm">
            <SurveyStep {...surveyStepProps} />
          </Tabs.Panel>

          <Tabs.Panel value="results" pt="sm">
            {/* ResultsStep is rendered here. It internally handles its content based on context */}
            {/* Ensure ResultsStep is only shown if canShowResults is true, tab takes care of this mostly */}
            {canShowResults && <ResultsStep />}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    );
  } else {
    // Desktop layout
    surveyAndResultsContent = (
      <Group align="flex-start" wrap="nowrap" gap="xl" p="md">
        <Stack style={{ width: 340, flexShrink: 0 }} gap="xl">
          <SurveyStep {...surveyStepProps} />
        </Stack>
        {canShowResults && (
          <Stack style={{ flexGrow: 1, minWidth: 0 }} gap="xl">
            <Button
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
              onClick={handleScheduleAppointment}
              leftSection={<IconCalendarStats size={20} />}
              rightSection={<IconArrowRight size={16} />}
              size="lg"
              disabled={isLoading}
              fullWidth
            >
              Ready to start? Book a consultation with our experts
            </Button>
            <ResultsStep />
          </Stack>
        )}
      </Group>
    );
  }

  return (
    <Center pt="xl">
      <LoansProvider
        userParsedData={userParsedData}
        partnerParsedData={partnerParsedData}
        surveyData={surveyDataForContext}
        userFile={userFile}
        partnerFile={partnerFile}
        setUserFile={setUserFile}
        setPartnerFile={setPartnerFile}
        userManualLoan={userManualLoan}
        partnerManualLoan={partnerManualLoan}
        isMobile={isMobile}
        hasAttemptedCalculation={canShowResults || false}
      >
        <Paper
          data-iframe-height
          ref={containerRef}
          shadow="none"
          radius={isMobile ? 0 : 'md'}
          style={{
            width: '100%',
            height: 'auto',
            minHeight: 'auto',
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            overflow: 'visible',
          }}
        >
          <LoadingOverlay
            visible={isLoading}
            zIndex={1000}
            loaderProps={{ size: 'md' }}
            overlayProps={{ blur: 1 }}
          />

          <FileErrorDisplay
            userError={userFileError}
            partnerError={partnerFileError}
            setUserFileError={setUserFileError}
            setPartnerFileError={setPartnerFileError}
          />

          {!isMobile && (
            <ScrollArea viewportProps={{ style: { overflowY: 'visible' } }}>
              {surveyAndResultsContent}
            </ScrollArea>
          )}
          {isMobile && surveyAndResultsContent}
        </Paper>
      </LoansProvider>
    </Center>
  );
}

export default App;
